import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IAttendee, IAuthTicketcode } from '@sv/interfaces';
import { map } from 'rxjs/operators';
import { customApp } from '../../_configuration/configuration';
import jwt_decode from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class TicketcodeAuthService {
  private token: string;
  private authStatusListener = new BehaviorSubject<boolean>(false);
  private isAuthenticated = false;
  private tokenTimer: any;

  constructor(private http: HttpClient) {}

  getToken() {
    return this.token;
  }

  decodeToken(): IAttendee {
    if (this.token) {
      try {
        const decodedToken: any = jwt_decode(this.token);
        return decodedToken.user;
      } catch (error) {
        console.log(error);
        return undefined;
      }
    }
  }

  setToken(token: string) {
    this.token = token;
  }

  getAuthStatusListener() {
    return this.authStatusListener.asObservable();
  }

  getIsAuthenticted() {
    return this.isAuthenticated;
  }

  autoAuthUser() {
    const authInformation = this.getAuthData();
    if (authInformation) {
      const now = new Date();
      const expiresIn = authInformation.expirationDate.getTime() - now.getTime();
      if (expiresIn > 0) {
        this.token = authInformation.token;

        return true;
      }
    }
    return false;
  }

  setAuthenticated() {
    const authInformation = this.getAuthData();
    if (authInformation) {
      const now = new Date();
      const expiresIn = authInformation.expirationDate.getTime() - now.getTime();
      if (expiresIn > 0) {
        this.token = authInformation.token;
        this.isAuthenticated = true;
        this.authStatusListener.next(true);
        this.setAuthTimer(expiresIn / 1000);
      }
    }
  }

  async emendoLogin(email: string, firstName: string, lastName: string, eventId: string) {
    const authData = {
      externalLogin: true,
      externalData: {
        email,
        firstName,
        lastName
      },
      eventId,
      username: '-',
      password: '-'
    };
    const token = await this.http.post<string>(customApp.url + '/app/login', authData).toPromise();
    const expiresInDuration = 12 * 60 * 60 * 1000;
    const now = new Date();
    const expirationDate = new Date(now.getTime() + expiresInDuration);
    this.token = token;
    this.saveAuthData(token, expirationDate);
    this.setAuthTimer(expiresInDuration);
    this.isAuthenticated = true;
    this.authStatusListener.next(this.isAuthenticated);
  }

  async promiseLogin(ticketcode: string, eventId: string, email?: string) {
    let authData: IAuthTicketcode;
    if (email) {
      authData = {
        ticketcode: '-',
        eventId,
        username: email,
        password: ticketcode
      };
    } else {
      authData = {
        ticketcode,
        eventId,
        username: '-',
        password: '-'
      };
    }
    const token = await this.http.post<string>(customApp.url + '/app/login', authData).toPromise();
    const expiresInDuration = 12 * 60 * 60 * 1000;
    const now = new Date();
    const expirationDate = new Date(now.getTime() + expiresInDuration);
    this.token = token;
    this.saveAuthData(token, expirationDate);
    this.setAuthTimer(expiresInDuration);
    this.isAuthenticated = true;
    this.authStatusListener.next(this.isAuthenticated);
  }

  login(ticketcode: string, eventId: string) {
    const authData: IAuthTicketcode = {
      ticketcode,
      eventId,
      username: '-',
      password: '-'
    };
    const result = this.http.post<string>(customApp.url + '/app/login', authData).pipe(
      map(async (response) => {
        const token = response;

        if (token) {
          const expiresInDuration = 12 * 60 * 60 * 1000;
          const now = new Date();
          const expirationDate = new Date(now.getTime() + expiresInDuration);

          this.token = token;
          this.isAuthenticated = true;
          this.saveAuthData(token, expirationDate);
          this.setAuthTimer(expiresInDuration);
          this.authStatusListener.next(true);
        }
      })
    );
    return result;
  }

  private setAuthTimer(duration: number) {
    this.tokenTimer = setTimeout(() => {}, duration * 1000);
  }

  private saveAuthData(token: string, expirationDate: Date) {
    localStorage.setItem('token', token);
    localStorage.setItem('expiration', expirationDate.toISOString());
  }

  logout() {
    this.token = null;
    this.isAuthenticated = false;
    this.authStatusListener.next(false);
    clearTimeout(this.tokenTimer);
  }

  private getAuthData() {
    const token = localStorage.getItem('token');
    const expirationDate = localStorage.getItem('expiration');
    if (!token || !expirationDate) {
      return;
    }
    return {
      token,
      expirationDate: new Date(expirationDate)
    };
  }
}
